import { AxiosRequestConfig } from 'axios';

import {
  GetOpportunityOptions,
  CreateOpportunityOptions,
  DeleteOpportunityOptions,
  UpdateOpportunityOptions,
  GetHistoricOpportunitysType,
  PatchHistoricOpportunityType,
  ProcessOptions,
  ProcessResponse,
} from '../modules/opportunity/api/types';

import { Response } from './types';

import axios from '@/lib/axios';
import {
  BoardPlan,
  CategoryFromCertificate,
  CreateUser,
  CriticalPath,
  Currency,
  EditUser,
  Elaboration,
  ExperienceTag,
  IssuingBody,
  Keyword,
  Location,
  MotiveLoss,
  Notification,
  Opportunity,
  ProductCNAE,
  ReasonApproval,
  ReasonDiscard,
  Research,
  ResultProcess,
  StageAfterDelivery,
  Subsidiary,
  User,
} from '@/types';

export const forgotPassword = ({ body }) => {
  return axios.unauthorized().post('/auth/forgot-password', body);
};

export const confirmForgotPassword = ({ body }) => {
  return axios.unauthorized().post('/auth/confirm-forgot-password', body);
};

export const getEffectiveCliente = ({
  queryKey,
}): Promise<ResponseListType<BaseEntity & { name: string }>> => {
  return axios.authorized().get('/effective-clients', { params: queryKey });
};

export const getOpportunities = ({
  queryKey,
}: {
  queryKey: any;
}): Promise<Response<Opportunity>> => {
  const config = {
    params: {
      ...queryKey[1],
    },
  };
  return axios.authorized().get(`/opportunitys`, config);
};

export const getOpportunityById = ({ id, config = {} }) => {
  return axios.authorized().get(`/opportunitys/${id}`, config);
};

export const getOpportunity = ({ id }: GetOpportunityOptions): Promise<Response<Opportunity>> => {
  return axios.authorized().get(`/searchs/${id}`);
};

export const createOpportunity = ({
  data,
}: CreateOpportunityOptions): Promise<Response<Opportunity>> => {
  return axios.authorized().post(`/opportunitys`, data);
};

export const updateOpportunity = ({
  id,
  data,
}: UpdateOpportunityOptions): Promise<Response<Opportunity>> => {
  return axios.authorized().patch(`/opportunitys/${id}`, data);
};

export const deleteOpportunity = ({ id }: DeleteOpportunityOptions) => {
  return axios.authorized().patch(`/searchs/${id}`);
};

export const getCriticalPaths = (): Promise<Response<CriticalPath>> => {
  return axios.authorized().get(`/critical-paths`);
};

export const getUsers = ({ queryKey }: any): Promise<Response<User>> => {
  return axios.authorized().get('/users', { params: queryKey });
};

export const createUser = (body: CreateUser) => {
  return axios.authorized().post('/users', body);
};

export const updateUser = ({ id, body }: { id: string; body: EditUser }) => {
  return axios.authorized().patch(`/users/${id}`, body);
};

export const deleteUser = (id: string): Promise<void> => {
  return axios.authorized().delete(`/users/${id}`);
};

export const getBoardPlans = ({
  id,
  queryKey,
}: {
  id?: string;
  queryKey: any;
}): Promise<ResponseListType<BoardPlan>> => {
  const route = id ? `/board-plans/${id}` : '/board-plans';
  return axios.authorized().get(route, { params: queryKey });
};

export const getBoards = ({
  id,
  queryKey,
}: {
  id?: string;
  queryKey: any;
}): Promise<ResponseListType<BaseEntity & { name: string }>> => {
  const route = id ? `/boards/${id}` : '/boards';
  return axios.authorized().get(route, { params: queryKey });
};

export const getBranches = ({ queryKey }): Promise<ResponseListType<BranchType>> => {
  return axios.authorized().get('/branchs', { params: queryKey });
};

export const getCostCenters = (params: AxiosRequestConfig) => {
  return axios.authorized().get('/cost-centers', { params });
};

export const getResultProcess = (id: string): Promise<Response<ResultProcess>> => {
  return axios.authorized().get('/result-process', {
    params: {
      processId: id,
    },
  });
};

export const getHistoricOpportunitys = (id: string): Promise<GetHistoricOpportunitysType> => {
  return axios.authorized().get('/historic-opportunitys', { params: { opportunitiesIds: [id] } });
};

export const patchHistoricOpportunity: PatchHistoricOpportunityType = ({ id, data }) => {
  return axios.authorized().patch(`/historic-opportunitys/${id}`, data);
};

export const getResourceSources = ({ queryKey }) => {
  return axios.authorized().get('/resource-sources', { params: queryKey });
};

export const getClients = ({
  id,
  queryKey,
}: {
  id?: string;
  queryKey: any;
}): Promise<ResponseListType<ClientType>> => {
  const route = id ? `/clients/${id}` : '/clients';
  return axios.authorized().get(route, { params: queryKey });
};

export const getExperienceTags = ({
  id,
  queryKey,
}: {
  id?: string;
  queryKey: any;
}): Promise<Response<ExperienceTag>> => {
  const route = id ? `/experience-tags/${id}` : '/experience-tags';
  return axios.authorized().get(route, { params: queryKey });
};

export const getLocations = ({
  id,
  queryKey,
}: {
  id?: string;
  queryKey: any;
}): Promise<Response<Location>> => {
  const route = id ? `/locations/${id}` : '/locations';
  return axios.authorized().get(route, { params: queryKey });
};

export const createProcess = ({ data }: { data: ProcessOptions }) => {
  return axios.authorized().post('/process', data);
};

export const getProcesses = ({ queryKey }: { queryKey: any }): Promise<ProcessResponse> => {
  const config = {
    params: {
      ...queryKey[1],
    },
  };
  return axios.authorized().get(`/process`, config);
};

export const patchOpportunity = ({
  id,
  nextContactDate,
}: {
  id: string;
  nextContactDate: string;
}) => {
  return axios.authorized().patch(`/opportunitys/${id}`, { nextContactDate });
};

export const patchUser = ({ id, favorites }: { id: string; favorites: string[] }): Promise<any> => {
  return axios.authorized().patch(`/users/${id}`, { favoritesIds: favorites });
};

export const getParticipants = ({
  queryKey,
}: {
  queryKey: any;
}): Promise<ResponseListType<ParticipantType>> => {
  const config = {
    params: {
      ...queryKey[1],
    },
  };
  return axios.authorized().get(`/participants`, config);
};

export const updateProcess = ({ id, data }): Promise<ProcessResponse> => {
  return axios.authorized().patch(`/process/${id}`, data);
};

export const getElaborations = ({ queryKey }: any): Promise<Response<Elaboration>> => {
  return axios.authorized().get('/elaborations', { params: queryKey });
};

export const getCurrencies = ({ queryKey }: any): Promise<Response<Currency>> => {
  return axios.authorized().get('/currencys', { params: queryKey });
};

export const getMotiveLoss = ({ queryKey }: any): Promise<Response<MotiveLoss>> => {
  return axios.authorized().get('/motive-loss', { params: queryKey });
};

export const getReasonApprovals = (): Promise<Response<ReasonApproval>> => {
  return axios.authorized().get('/reason-approvals');
};

export const getResearches = ({ queryKey }: { queryKey: any }): Promise<Response<Research>> => {
  const config = {
    params: {
      ...queryKey[1],
    },
  };
  return axios.authorized().get(`/searchs`, config);
};

export const getResearch = ({ id }: { id: string }): Promise<Response<Research>> => {
  return axios.authorized().get(`/searchs/${id}`);
};

export const getTopResearches = ({ config = {} }: { config?: AxiosRequestConfig } = {}): Promise<
  Response<any>
> => {
  return axios.authorized().get(`/top-searchs`, config);
};

export const getReasonDiscards = (): Promise<Response<ReasonDiscard>> => {
  return axios.authorized().get(`/reason-discards`);
};

export const getKeywords = (page?: PageType, queryKey?: any): Promise<Response<Keyword>> => {
  return axios.authorized().get('/keywords', { params: { ...page, ...queryKey } });
};

export const getDashboard = ({ queryKey }) => {
  return axios.authorized().get(`/dashboard`, { params: queryKey });
};

export const getStageAfterDelivery = ({ queryKey }): Promise<Response<StageAfterDelivery>> => {
  return axios.authorized().get('/stage-after-delivery', { params: queryKey });
};

export const getCategories = ({ queryKey }): Promise<ResponseListType<CategoryType>> => {
  return axios.authorized().get('/category-from-certificates', { params: queryKey });
};

export const getIssuingBodys = (params: any): Promise<ResponseListType<IssuingBodyType>> => {
  return axios.authorized().get('/issuing-bodys', { params });
};

export const getProductsCNAE = (): Promise<Response<ProductCNAE>> => {
  return axios.authorized().get('/product-cnaes');
};

export const getCompanies = ({ queryKey }: any): Promise<ResponseListType<CompanyType>> => {
  return axios.authorized().get('/companys', { params: queryKey });
};

export const postCompany = (data: Partial<CompanyCreateType>): Promise<Response<CompanyType>> => {
  return axios.authorized().post('/companys', data);
};

export const patchCompany = ({
  data,
  id,
}: {
  id: string;
  data: Partial<CompanyCreateType>;
}): Promise<Response<CompanyType>> => {
  return axios.authorized().patch(`/companys/${id}`, data);
};

export const deleteCompany = (id: string): Promise<Response<CompanyType>> => {
  return axios.authorized().delete(`/companys/${id}`);
};

export const getSubsidiaries = (): Promise<Response<Subsidiary>> => {
  return axios.authorized().get('/subsidiarys');
};

export const getIssuingBodies = ({ queryKey }): Promise<Response<IssuingBody>> => {
  return axios.authorized().get('/issuing-bodys', { params: queryKey });
};

export const getCategoriesFromCertificates = (): Promise<Response<CategoryFromCertificate>> => {
  return axios.authorized().get('/category-from-certificates');
};

export const getRenewalMethods = ({ queryKey }): Promise<ResponseListType<RenewalMethodType>> => {
  return axios.authorized().get('/renewal-methods', { params: queryKey });
};

export const patchParticipant = ({
  id,
  data,
}: {
  id: string;
  data: Partial<ParticipantCreateType>;
}): Promise<any> => {
  return axios.authorized().patch(`/participants/${id}`, data);
};

export const postParticipant = (data: Partial<ParticipantCreateType>): Promise<any> => {
  return axios.authorized().post('/participants', data);
};

export const deleteParticipant = (id: string): Promise<any> => {
  return axios.authorized().delete(`/participants/${id}`);
};

export const getNotifications = ({
  take,
  skip,
  enabled,
  numberComlicitacaoSearch,
  fileNumber,
}: {
  take: number;
  skip: number;
  enabled: boolean;
  numberComlicitacaoSearch?: number;
  fileNumber?: string;
}): Promise<Response<Notification>> => {
  return axios.authorized().get('/notifications', {
    params: { take, skip, enabled, numberComlicitacaoSearch, fileNumber },
  });
};

export const updateNotification = ({
  id,
  body,
}: {
  id: string;
  body: { enabled: boolean };
}): Promise<Notification> => {
  return axios.authorized().patch(`/notifications/${id}`, body);
};

export const uploadDatasheet = ({ data, id }: { data: FormData; id: string }) => {
  return axios.authorized().patch(`/process/${id}/datasheet`, data, {
    headers: {
      'content-type': 'multipart/form-data',
    },
  });
};

export const patchContact = ({
  id,
  data,
}: {
  id: string;
  data: Partial<ContactCreateType>;
}): Promise<any> => {
  return axios.authorized().patch(`/contacts/${id}`, data);
};

export const postContact = (data: Partial<ContactCreateType>): Promise<any> => {
  return axios.authorized().post('/contacts', data);
};

export const deleteContact = (id: string): Promise<any> => {
  return axios.authorized().delete(`/contacts/${id}`);
};

export const getContacts = ({
  queryKey,
}: {
  queryKey: any;
}): Promise<ResponseListType<ContactType>> => {
  const config = {
    params: {
      ...queryKey[1],
    },
  };
  return axios.authorized().get(`/contacts`, config);
};

export const getTecnicalCommercial = ({
  queryKey,
}: {
  queryKey: any;
}): Promise<ResponseListType<BaseEntity & { name: string }>> => {
  const config = {
    params: {
      ...queryKey[1],
    },
  };
  return axios.authorized().get(`/tecnical-and-commecial-representants`, config);
};
